<div class="vl-page animate-fade-in">

  <!-- ─── Header ─────────────────────────────────────────────────────────── -->
  <header class="vl-header">
    <div class="vl-header__title">
      <svg class="vl-header__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      <div>
        <h1 class="vl-header__headline">Video Library</h1>
        <p class="vl-header__sub">{{ total() }} video{{ total() === 1 ? '' : 's' }}</p>
      </div>
    </div>

    <div class="vl-header__actions">
      <!-- Search -->
      <div class="vl-search">
        <svg class="vl-search__icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <input
          type="text"
          placeholder="Search by title or tag…"
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearch()"
          class="vl-search__input"
          aria-label="Search videos"
        />
      </div>

      <!-- Batch delete -->
      @if (selectedCount() > 0) {
        <button (click)="batchDelete()" [disabled]="isBatchDeleting()" class="vl-btn vl-btn--danger-ghost">
          @if (isBatchDeleting()) {
            <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          }
          Delete {{ selectedCount() }} selected
        </button>
      }

      <!-- Add button -->
      <button (click)="openAddPanel()" class="vl-btn vl-btn--create">
        <svg class="vl-btn__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Upload Video
      </button>
    </div>
  </header>

  <!-- ─── Error banner ───────────────────────────────────────────────────── -->
  @if (errorMsg()) {
    <div class="vl-alert-error" role="alert">
      <span>{{ errorMsg() }}</span>
      <button (click)="errorMsg.set(null)" class="vl-alert-error__close" aria-label="Dismiss">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  }

  <!-- ─── Content ───────────────────────────────────────────────────────── -->
  <div class="vl-content">

    @if (isLoading() && videos().length === 0) {
      <div class="vl-state-center">
        <svg class="vl-spinner vl-spinner--lg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Loading…
      </div>
    } @else if (videos().length === 0) {
      <div class="vl-state-center vl-state-center--col">
        <svg class="vl-empty-icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
        <p class="vl-empty-title">No videos yet</p>
        <p class="vl-empty-sub">Add your first YouTube video to get started</p>
      </div>
    } @else {
      <!-- Select all bar -->
      <div class="vl-select-bar">
        <label class="vl-checkbox-label">
          <input
            type="checkbox"
            [checked]="allSelected()"
            (change)="toggleSelectAll()"
            class="vl-checkbox"
          />
          Select all
        </label>
        <span class="vl-select-bar__count">{{ total() }} total</span>
      </div>

      <!-- Grid -->
      <div class="vl-grid">
        @for (video of videos(); track video.id) {
          <div class="vl-card" [class.vl-card--selected]="isSelected(video.id)">

            <!-- Thumbnail -->
            <div class="vl-card__thumb-wrap">
              <img
                [src]="video.thumbnail_url"
                [alt]="video.title"
                class="vl-card__thumb"
                loading="lazy"
              />
              <!-- Play overlay → opens YouTube -->
              <a
                [href]="youtubeWatchUrl(video)"
                target="_blank"
                rel="noopener noreferrer"
                class="vl-card__play-overlay"
                [attr.aria-label]="'Watch ' + video.title + ' on YouTube'"
              >
                <div class="vl-card__play-btn">
                  <svg class="w-7 h-7 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8 5v14l11-7L8 5z"/>
                  </svg>
                </div>
              </a>
              <!-- Checkbox -->
              <div class="vl-card__checkbox-wrap">
                <input
                  type="checkbox"
                  [checked]="isSelected(video.id)"
                  (change)="toggleSelect(video.id)"
                  class="vl-checkbox"
                  [attr.aria-label]="'Select ' + video.title"
                  (click)="$event.stopPropagation()"
                />
              </div>
            </div>

            <!-- Info -->
            <div class="vl-card__body">
              <p class="vl-card__title" [title]="video.title">{{ video.title }}</p>
              @if (video.tags.length > 0) {
                <div class="vl-card__tags">
                  @for (tag of video.tags; track tag) {
                    <span class="vl-badge">{{ tag }}</span>
                  }
                </div>
              }
            </div>

            <!-- Actions -->
            <div class="vl-card__actions">
              <button
                (click)="generateThumbnail(video)"
                [disabled]="generatingFor(video.id)"
                class="vl-icon-btn"
                aria-label="Generate thumbnail from title"
                [title]="generatingFor(video.id) ? 'Generating…' : 'Generate thumbnail (Flux)'"
              >
                @if (generatingFor(video.id)) {
                  <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                } @else {
                  <!-- sparkle icon -->
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 3l1.5 4.5L11 9l-4.5 1.5L5 15l-1.5-4.5L-1 9l4.5-1.5L5 3zM18 4l.9 2.7L21.6 8l-2.7.9L18 11.6l-.9-2.7L14.4 8l2.7-1.3L18 4zM14 13l1.2 3.6L19 18l-3.8 1.4L14 23l-1.2-3.6L9 18l3.8-1.4L14 13z"/>
                  </svg>
                }
              </button>
              <button
                (click)="resetThumbnail(video)"
                [disabled]="resettingFor(video.id)"
                class="vl-icon-btn"
                aria-label="Reset thumbnail to YouTube"
                [title]="resettingFor(video.id) ? 'Resetting…' : 'Reset thumbnail (YouTube)'"
              >
                @if (resettingFor(video.id)) {
                  <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                } @else {
                  <!-- reset icon -->
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v6h6M20 20v-6h-6"/>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5.5 18.5A8 8 0 0018.5 5.5M18.5 18.5A8 8 0 005.5 5.5"/>
                  </svg>
                }
              </button>
              <button
                (click)="openSetThumb(video)"
                class="vl-icon-btn"
                aria-label="Set custom thumbnail"
                title="Set custom thumbnail URL"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </button>
              <button (click)="openEdit(video)" class="vl-icon-btn" aria-label="Edit">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
              <a
                [href]="youtubeWatchUrl(video)"
                target="_blank"
                rel="noopener noreferrer"
                class="vl-icon-btn"
                aria-label="Open on YouTube"
              >
                <!-- YouTube icon -->
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <button (click)="openDelete(video)" class="vl-icon-btn vl-icon-btn--danger" aria-label="Delete">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>

          </div>
        }
      </div>

      <!-- Load more -->
      @if (hasMore()) {
        <div class="vl-load-more">
          <button (click)="loadMore()" [disabled]="isLoading()" class="vl-btn vl-btn--ghost">
            @if (isLoading()) { Loading… } @else { Load more }
          </button>
        </div>
      }
    }
  </div>
</div>

<!-- ─── Add Video Panel ────────────────────────────────────────────────────── -->
@if (showAddPanel()) {
  <div class="vl-modal-backdrop" [class.vl-modal-backdrop--closing]="addClosing()" (click)="closeAddPanel()" role="dialog" aria-modal="true" aria-label="Add YouTube Video">
    <div class="vl-modal vl-modal--sm" [class.vl-modal--closing]="addClosing()" (click)="$event.stopPropagation()">
      <div class="vl-modal__header">
        <h2 class="vl-modal__title">Add YouTube Video</h2>
        <button (click)="closeAddPanel()" [disabled]="isAdding()" class="vl-modal__close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="vl-modal__body">
        @if (addError()) {
          <div class="vl-alert-error" role="alert"><span>{{ addError() }}</span></div>
        }
        <div class="vl-form-group">
          <label class="vl-form-label" for="add-url">YouTube URL <span class="vl-form-label__req">*</span></label>
          <input
            id="add-url"
            type="url"
            [(ngModel)]="addUrl"
            class="vl-form-input"
            placeholder="https://www.youtube.com/watch?v=…"
            [disabled]="isAdding()"
          />
          <p class="vl-form-hint">Supports watch, youtu.be, Shorts, and embed links</p>
        </div>
        <div class="vl-form-group">
          <label class="vl-form-label" for="add-title">Title <span class="vl-form-label__hint">(optional — auto-filled if empty)</span></label>
          <input
            id="add-title"
            type="text"
            [(ngModel)]="addTitle"
            class="vl-form-input"
            placeholder="e.g. Brand Intro Video"
            [disabled]="isAdding()"
          />
        </div>
        <div class="vl-form-group">
          <label class="vl-form-label" for="add-tags">Tags <span class="vl-form-label__hint">(comma-separated)</span></label>
          <input
            id="add-tags"
            type="text"
            [(ngModel)]="addTags"
            class="vl-form-input"
            placeholder="e.g. brand, intro, promo"
            [disabled]="isAdding()"
          />
        </div>
      </div>

      <div class="vl-modal__footer">
        <div class="vl-modal__footer-actions">
          <button (click)="closeAddPanel()" [disabled]="isAdding()" class="vl-btn vl-btn--ghost">Cancel</button>
          <button (click)="submitAdd()" [disabled]="isAdding()" class="vl-btn vl-btn--primary">
            @if (isAdding()) {
              <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Adding…
            } @else {
              Add Video
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Edit Modal ─────────────────────────────────────────────────────────── -->
@if (editingVideo()) {
  <div class="vl-modal-backdrop" (click)="closeEdit()" role="dialog" aria-modal="true" aria-label="Edit Video">
    <div class="vl-modal vl-modal--sm" (click)="$event.stopPropagation()">
      <div class="vl-modal__header">
        <h2 class="vl-modal__title">Edit Video</h2>
        <button (click)="closeEdit()" [disabled]="isSavingEdit()" class="vl-modal__close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="vl-modal__body">
        @if (editError()) {
          <div class="vl-alert-error" role="alert"><span>{{ editError() }}</span></div>
        }
        <div class="vl-form-group">
          <label class="vl-form-label" for="edit-title">Title</label>
          <input id="edit-title" type="text" [(ngModel)]="editTitle" class="vl-form-input" placeholder="Video title" [disabled]="isSavingEdit()" />
        </div>
        <div class="vl-form-group">
          <label class="vl-form-label" for="edit-tags">Tags <span class="vl-form-label__hint">(comma-separated)</span></label>
          <input id="edit-tags" type="text" [(ngModel)]="editTags" class="vl-form-input" placeholder="e.g. brand, intro" [disabled]="isSavingEdit()" />
        </div>
      </div>
      <div class="vl-modal__footer">
        <div class="vl-modal__footer-actions">
          <button (click)="closeEdit()" [disabled]="isSavingEdit()" class="vl-btn vl-btn--ghost">Cancel</button>
          <button (click)="saveEdit()" [disabled]="isSavingEdit()" class="vl-btn vl-btn--primary">
            @if (isSavingEdit()) {
              <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
            }
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Set Thumbnail URL Modal ───────────────────────────────────────────── -->
@if (thumbVideo()) {
  <div class="vl-modal-backdrop" (click)="closeSetThumb()" role="dialog" aria-modal="true" aria-label="Set custom thumbnail">
    <div class="vl-modal vl-modal--sm" (click)="$event.stopPropagation()">
      <div class="vl-modal__header">
        <h2 class="vl-modal__title">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          Set Cover Photo
        </h2>
        <button (click)="closeSetThumb()" [disabled]="isSavingThumb()" class="vl-modal__close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="vl-modal__body">
        @if (thumbError()) {
          <div class="vl-alert-error" role="alert"><span>{{ thumbError() }}</span></div>
        }

        <!-- Preview -->
        <div class="vl-thumb-preview">
          @if (thumbPreviewUrl()) {
            <img [src]="thumbPreviewUrl()!" alt="Thumbnail preview" class="vl-thumb-preview__img" />
          } @else {
            <div class="vl-thumb-preview__empty" aria-hidden="true">
              <svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 11.25h.008v.008H18V11.25zm-3 9H6.75A2.25 2.25 0 014.5 18V6.75A2.25 2.25 0 016.75 4.5h10.5A2.25 2.25 0 0119.5 6.75v10.5A2.25 2.25 0 0117.25 20.25z"/>
              </svg>
              <span>No preview</span>
            </div>
          }
        </div>

        <div class="vl-form-group">
          <label class="vl-form-label" for="thumb-url">Image URL <span class="vl-form-label__req">*</span></label>
          <input
            id="thumb-url"
            type="url"
            [(ngModel)]="thumbUrl"
            (ngModelChange)="onThumbUrlChange()"
            class="vl-form-input"
            placeholder="https://example.com/image.jpg"
            [disabled]="isSavingThumb()"
            autocomplete="off"
          />
          <p class="vl-form-hint">Paste any publicly accessible image URL. The preview updates as you type.</p>
        </div>
      </div>

      <div class="vl-modal__footer">
        <div class="vl-modal__footer-actions">
          <button (click)="closeSetThumb()" [disabled]="isSavingThumb()" class="vl-btn vl-btn--ghost">Cancel</button>
          <button (click)="saveThumb()" [disabled]="isSavingThumb() || !thumbUrl.trim()" class="vl-btn vl-btn--primary">
            @if (isSavingThumb()) {
              <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Saving…
            } @else {
              Save
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Delete Confirm Modal ──────────────────────────────────────────────── -->
@if (videoToDelete()) {
  <div class="vl-modal-backdrop" (click)="closeDelete()" role="dialog" aria-modal="true" aria-label="Delete video">
    <div class="vl-modal vl-modal--sm" (click)="$event.stopPropagation()">
      <div class="vl-delete-body">
        <div class="vl-delete-icon">
          <svg class="w-6 h-6 text-[#f87171]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
          </svg>
        </div>
        <h3 class="vl-delete-title">Remove video?</h3>
        <p class="vl-delete-sub">
          "<span class="text-white font-medium">{{ videoToDelete()?.title }}</span>" will be removed from the library.
        </p>
      </div>
      <div class="vl-modal__footer vl-modal__footer--centered">
        <button (click)="closeDelete()" [disabled]="isDeleting()" class="vl-btn vl-btn--ghost vl-btn--flex">Cancel</button>
        <button (click)="confirmDelete()" [disabled]="isDeleting()" class="vl-btn vl-btn--danger vl-btn--flex">
          @if (isDeleting()) {
            <svg class="vl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          }
          Remove
        </button>
      </div>
    </div>
  </div>
}
